import type { SimpleBlogItem } from './BlogListProvider';

const AI_BLOG_POSTS_API_URL = 'https://smartdataproxy.azurewebsites.net/api/AIBlogPosts';

/**
 * Raw item shape from the AIBlogPosts API (contentSources is a comma-separated string).
 */
interface AIBlogPostRawItem {
    id: number;
    title: string;
    description: string;
    keywords: string;
    country: string;
    city: string;
    blogUrl: string;
    logoUrl: string;
    contactName: string;
    ownerEMailAddress: string;
    contentSources: string;
    approved: boolean;
    created: string | null;
    modified: string | null;
}

function mapRawToSimpleBlogItem(raw: AIBlogPostRawItem): SimpleBlogItem {
    const contentSourcesArray =
        typeof raw.contentSources === 'string' && raw.contentSources.trim()
            ? raw.contentSources.split(';').map((s) => s.trim()).filter(Boolean)
            : [];

    return {
        id: raw.id,
        title: raw.title,
        description: raw.description,
        keywords: raw.keywords,
        country: raw.country,
        city: raw.city,
        blogUrl: raw.blogUrl,
        logoUrl: raw.logoUrl,
        contactName: raw.contactName,
        ownerEMailAddress: raw.ownerEMailAddress,
        created: raw.created ? new Date(raw.created) : null,
        modified: raw.modified ? new Date(raw.modified) : null,
        approved: raw.approved,
        contentSources: contentSourcesArray,
    };
}

/**
 * Singleton data manager for AI blog posts.
 * Fetches from the AIBlogPosts API with optional pagination (page, pageSize).
 */
export class AIBlogContent {
    private static instance: AIBlogContent;

    private constructor() {}

    static getInstance(): AIBlogContent {
        if (!AIBlogContent.instance) {
            AIBlogContent.instance = new AIBlogContent();
        }
        return AIBlogContent.instance;
    }

    /**
     * Gets AI blog posts from the API.
     * @param page - Current page (query parameter: page).
     * @param pageSize - Number of items per page (query parameter: pageSize).
     * @returns Array of blog items mapped to SimpleBlogItem (contentSources string split into array).
     */
    async GetData(page?: number, pageSize?: number): Promise<SimpleBlogItem[]> {
        const params = new URLSearchParams();
        if (page != null) {
            params.set('page', String(page));
        }
        if (pageSize != null) {
            params.set('pageSize', String(pageSize));
        }
        const queryString = params.toString();
        const url = queryString ? `${AI_BLOG_POSTS_API_URL}?${queryString}` : AI_BLOG_POSTS_API_URL;

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`AIBlogPosts API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const rawItems = Array.isArray(data) ? (data as AIBlogPostRawItem[]) : [];
        return rawItems.map(mapRawToSimpleBlogItem);
    }

    /**
     * Loads a single AI blog post by ID.
     * @param id - The blog post ID (query parameter: ID).
     * @returns The blog item, or null if not found.
     */
    async GetById(id: number): Promise<SimpleBlogItem | null> {
        const params = new URLSearchParams();
        params.set('ID', String(id));
        const url = `${AI_BLOG_POSTS_API_URL}?${params.toString()}`;

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`AIBlogPosts API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const rawItems = Array.isArray(data) ? (data as AIBlogPostRawItem[]) : [];
        const item = rawItems[0];
        return item ? mapRawToSimpleBlogItem(item) : null;
    }
}
